import React, { useEffect, useState } from "react";
import { Search, Edit, Trash2 } from "lucide-react";
import PopoverMenu, { MenuItem } from "@/components/ui/Popeover";
import Layout from "@/components/layout/Layout";
import TextField from "@/components/ui/Textfield";
import Dropdown from "@/components/ui/DropDown";
import useCampaignListStore from "./store/campaignList";
import { useShallow } from "zustand/react/shallow";
import { getColorByHash } from "@/helper/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import { BiTrash } from "react-icons/bi";
import CompanyLogo from "@/assets/company-logo.png";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";

const CampaignList: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [clientFilter, setClientFilter] = useState("Filter by Client");
  const [dateFilter, setDateFilter] = useState("Filter by Date");
  const [campaignToDelete, setCampaignToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const { campaignList, getCampaignList, deleteCampaign, recoverCampaign } =
    useCampaignListStore(
      useShallow((state) => ({
        campaignList: state.campaignList,
        getCampaignList: state.getCampaignList,
        deleteCampaign: state.deleteCampaign,
        recoverCampaign: state.recoverCampaign,
      }))
    );

  const { showError, showSuccess } = useGlobalStore();

  const handleDelete = async () => {
    if (campaignToDelete) {
      try {
        await deleteCampaign(campaignToDelete.id);
        setCampaignToDelete(null);
      } catch (err) {
        console.error("Failed to delete campaign", err);
      }
    }
  };

  const createMenuItems = (
    campaignId: string,
    campaignName: string
  ): MenuItem[] => [
    {
      label: "Edit Campaign",
      icon: <Edit className="w-4 h-4" />,
      onClick: () => navigate(`/campaign?id=${campaignId}`),
    },
    {
      label: "Delete Campaign",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () =>
        setCampaignToDelete({ id: campaignId, name: campaignName }),
    },
  ];

  const createMenuItems1 = (campaignId: string): MenuItem[] => [
    {
      label: "Recover Campaign",
      icon: <Edit className="w-4 h-4" />,
      onClick: async () => {
        try {
          await recoverCampaign(campaignId);
          showSuccess("Campaign Recover Succesfully");
        } catch (error) {
          showError(error.message ?? "Something went wrong");
        }
      },
    },
  ];

  const filteredGroupedCampaigns = Object.entries(campaignList).reduce(
    (acc, [groupName, campaigns]) => {
      const filteredCampaigns = campaigns.filter(
        (campaign) =>
          campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          campaign.id.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (filteredCampaigns.length > 0) {
        acc[groupName] = filteredCampaigns;
      }
      return acc;
    },
    {} as typeof campaignList
  );

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const type = searchParams.get("type");
      if (type === "archived") {
        await getCampaignList([{ key: "include_deleted", value: "true" }]);
      } else {
        await getCampaignList();
      }
    } catch (err: any) {
      showError(err.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchParams, getCampaignList]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Campaign List</h1>
          <div className="flex items-center space-x-4">
            <TextField
              type="text"
              placeholder="Search by Keyword, project, #"
              leadingIcon={<Search size={15} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Dropdown
              selected={clientFilter}
              setSelected={(e) => setClientFilter(e.toString())}
              options={[]}
            />
            <Dropdown
              selected={dateFilter}
              setSelected={(e) => setDateFilter(e.toString())}
              options={[]}
            />
          </div>
        </div>

        <div className="flex justify-end mb-6">
          <button
            className="text-gray-500 hover:text-gray-700 underline text-sm"
            onClick={() => {
              if (searchParams.get("type") === "archived") {
                searchParams.delete("type");
                setSearchParams(searchParams);
              } else {
                setSearchParams({
                  ...searchParams,
                  type: "archived",
                });
              }
            }}
          >
            {searchParams.get("type") === "archived"
              ? "Hide Archived Campaigns"
              : "See Archived Campaigns"}
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(filteredGroupedCampaigns).map(
              ([groupName, campaigns]) => (
                <div key={groupName}>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    {groupName}
                  </h2>
                  <div className="space-y-2">
                    {campaigns.map((campaign) => (
                      <React.Fragment key={campaign.id}>
                        <div className="flex items-center justify-between rounded-lg py-2">
                          <div className="flex items-center">
                            {/* <div
                            className={`w-12 h-12 rounded-lg ${getColorByHash(
                              campaign.name
                            )} flex items-center justify-center text-xl mr-7`}
                          ></div> */}
                            <img
                              src={CompanyLogo}
                              className="w-12 h-12 rounded-lg mr-7"
                            />
                            <div className="pr-10">
                              <span className="text-sm text-gray-500 font-medium">
                                {campaign.project_code}
                              </span>
                            </div>
                            <div>
                              <h3 className="text-md font-medium text-gray-900">
                                {campaign.name}
                              </h3>
                            </div>
                          </div>
                          <PopoverMenu
                            items={
                              searchParams.get("type") === "archived"
                                ? createMenuItems1(campaign.id)
                                : createMenuItems(campaign.id, campaign.name)
                            }
                          />
                        </div>
                        <hr />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        )}

        {Object.keys(filteredGroupedCampaigns).length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-base">
              No campaigns found matching your search criteria.
            </p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!campaignToDelete}
        onOpenChange={() => setCampaignToDelete(null)}
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Delete Campaign
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{campaignToDelete?.name}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button
              type="secondary"
              trailingIcon={<BiTrash fontSize={18} />}
              onClick={handleDelete}
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CampaignList;
